/**
 * The PC's half of Dispatch, in Assist's own window on a PC (docs/dispatch.md).
 * The desktop app is the PC: it publishes itself and its tools, keeps the
 * hub open as itself, and when a reply in its thread waits on its tools, takes
 * it on the way a chat open on this page would, and lets the Worker know how
 * it is getting on.
 *
 * Plain timers, promises and the bridge's own signals, never visibility,
 * focus or animation frames: the desktop app keeps this page alive in the tray
 * with its window hidden, and a hidden page gets none of those.
 *
 * Everything outside the object, the bridge, the Worker and the turn store,
 * comes in as `RunnerDeps`, so the states a reply goes through are tested
 * without any of them.
 *
 *   wake    →  waking  (read the thread; anything but the reply waiting on
 *                       this PC, ignore)
 *           →  running (reported, which clears why it waited; the continue
 *                       is on its way, and a 403 publishes again and tries
 *                       once more)
 *   asking  →  reported while a sheet on this PC asks the person, with the
 *              shell's request that a paired phone may answer it, and
 *              running again once it is answered
 *   cancel  →  let go of: the thread's windows (`chatStopped`), then no
 *              continue, no further call, `replyEnded`
 *
 * A phone's pairing and a paired phone's answers come on the hub and go to
 * the shell as they came, which alone checks them (docs/dispatch.md,
 * "Approving from a paired phone"); the page only delivers.
 */

import {
  ApiError,
  type ApprovalRequest,
  type Chat,
  type ChatSummary,
  type ClientTool,
  type Device,
  type DevicePut,
  type DeviceReport,
  type HubServerFrame,
  type Message
} from './api';
import { waitsOnPc } from './components/dispatchText';
import { splitRunId, type DeviceInfo, type ShellVerdict } from './desktop';
import { backoffMs } from './hub';

export type ApprovalFrame = Extract<HubServerFrame, { t: 'approval' }>;
export type PairFrame = Extract<HubServerFrame, { t: 'pair' }>;

/** What the runner looks at of a chat's turn. */
export interface TurnView {
  phase: 'streaming' | 'done' | 'stopped' | 'failed';
  messageId: string | null;
  failure: { code: string; refused: boolean } | null;
}

export interface RunnerDeps {
  /** This PC, from the bridge; null where the app does not run Dispatch. */
  info: () => Promise<DeviceInfo | null>;
  /** The tools the window offers a turn. */
  tools: () => Promise<ClientTool[] | undefined>;
  put: (id: string, body: DevicePut) => Promise<Device>;
  getChat: (id: string) => Promise<Chat>;
  report: (id: string, body: DeviceReport) => Promise<void>;
  /** Start the continue; false where nothing was started (taken on already,
   *  or not this page's to take). */
  takeOn: (chat: Chat, message: Message, again: boolean) => boolean;
  /** The turn this page holds in a chat, if any. */
  turn: (chatId: string) => TurnView | null;
  /** Called with the chat's turn each time it changes; returns the unsubscribe. */
  watch: (chatId: string, fn: (turn: TurnView | null) => void) => () => void;
  /** Stop reading a reply and running its calls; nothing is sent. */
  abandon: (chatId: string, messageId: string | null) => void;
  /** The shell is told the reply is over. */
  ended: (messageId: string) => void;
  /** The shell lets go, for good, of the windows the thread holds. */
  stopped: (chatId: string) => void;
  /** The shell says the PC is working on a task from the phone. */
  dispatched: (messageId: string) => void;
  /** The reply is about to be taken on from a wake: its calls are named to
   *  the shell as dispatched, never as its chat's. */
  fromWake: (messageId: string) => void;
  /** A paired phone's answer, to the shell to check. */
  approve: (frame: ApprovalFrame) => Promise<ShellVerdict | null>;
  /** A phone asking to be paired, to the shell to check; null where the
   *  shell has no `pair`, or did not answer. */
  pair: (frame: PairFrame) => Promise<ShellVerdict | null>;
  /** What the shell made of a phone's pairing, to the Worker. */
  answerPairing: (id: string, phone: string, ok: boolean) => Promise<void>;
  /** Why the Worker will not have this PC, or null once a publish goes
   *  through. Sending again would not change it, so it is shown, not retried. */
  refused: (message: string | null) => void;
}

/** How long tools may keep changing before the PC publishes them. */
export const REPUBLISH_MS = 500;
/** How many times a changed manifest is sent before the next change. */
const REPUBLISH_TRIES = 3;

/** A refusal that sending again could cure: no answer, a busy Worker. */
export const retryable = (err: unknown): boolean =>
  !(err instanceof ApiError) || err.status === 0 || err.status === 429 || err.status >= 500;

const status = (err: unknown): number => (err instanceof ApiError ? err.status : 0);
/** The Worker's own words for a refusal, which name what to change. */
const refusalText = (err: unknown): string => (err instanceof Error && err.message ? err.message : 'The Worker refused it.');
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** A reply being taken on, between `wake` and the continue being on its way. */
interface Taking {
  chatId: string;
  state: 'waking' | 'running';
  /** The refusal that was answered with a new publish. */
  retried: boolean;
  unwatch: (() => void) | null;
}

export class DeviceRunner {
  private info: DeviceInfo | null = null;
  private chatId: string | null = null;
  private stopped = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Replies being taken on, by message. */
  private taking = new Map<string, Taking>();
  /** Reports go in the order they were made. */
  private reports: Promise<unknown> = Promise.resolve();
  /** The shell's requests a paired phone may answer, by the id the call was
   *  run under, until that call's sheet is answered. */
  private approvals = new Map<string, ApprovalRequest>();
  /** The calls whose sheet is asking now, by run id. */
  private askingNow = new Set<string>();

  constructor(private readonly deps: RunnerDeps) {}

  /** This PC's thread, as far as the runner knows. */
  get thread(): string | null {
    return this.chatId;
  }

  /** The messages being taken on, and where each stands. */
  states(): Record<string, Taking['state']> {
    return Object.fromEntries([...this.taking].map(([id, t]) => [id, t.state]));
  }

  /**
   * Publish this PC, and tell `onPc` which it is once the Worker has it, so
   * the hub opens as it: `null` for a page that is not one, and for one
   * whose publish has not gone through yet — the hub opens plain meanwhile,
   * and again as the PC when a later try does.
   */
  async start(onPc: (id: string | null) => void): Promise<void> {
    const info = await this.deps.info();
    if (this.stopped) return;
    if (!info) return onPc(null);
    this.info = info;
    for (let attempt = 0; !this.stopped; attempt++) {
      try {
        await this.publish();
        return onPc(info.id);
      } catch (err) {
        if (attempt === 0) onPc(null);
        if (!retryable(err)) return this.deps.refused(refusalText(err));
        await sleep(backoffMs(attempt));
      }
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    for (const t of this.taking.values()) t.unwatch?.();
    this.taking.clear();
  }

  /** A folder was granted, a server started or stopped: the tools changed.
   *  Published once they settle. */
  toolsChanged(): void {
    if (!this.info || this.stopped) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.republish(), REPUBLISH_MS);
  }

  /** A PC was forgotten, as the hub tells every page. This one, while it is
   *  on: it comes back at once with no thread, as it would at its next
   *  start, rather than staying on and unknown until then. */
  gone(deviceId: string): void {
    if (!this.info || this.stopped || deviceId !== this.info.id) return;
    this.chatId = null;
    void this.republish();
  }

  /** The thread changed, as the hub tells every page. */
  dispatch(chat: Pick<ChatSummary, 'id' | 'deviceId'>): void {
    if (this.info && chat.deviceId === this.info.id) this.chatId = chat.id;
  }

  /** A reply in this PC's thread waits on its tools. */
  async wake(frame: { chatId: string; messageId: string }): Promise<void> {
    const { chatId, messageId } = frame;
    const info = this.info;
    if (!info || this.stopped || this.taking.has(messageId)) return;
    // This page holds a stream in the thread: the reply's next pause arrives
    // there as `tool_request` and is taken on from there.
    if (this.deps.turn(chatId)?.phase === 'streaming') return;

    const taking: Taking = { chatId, state: 'waking', retried: false, unwatch: null };
    this.taking.set(messageId, taking);
    const still = () => this.taking.get(messageId) === taking;
    try {
      const chat = await this.deps.getChat(chatId);
      if (!still()) return;
      const message = chat.messages[chat.messages.length - 1];
      // Anything but the reply waiting on this PC: the wake is stale.
      if (chat.deviceId !== info.id || message?.id !== messageId || !waitsOnPc(message)) {
        this.taking.delete(messageId);
        return;
      }
      this.chatId = chat.id;
      taking.state = 'running';
      const goOn = await this.reportRunning(messageId);
      if (!still()) return;
      if (!goOn) {
        this.taking.delete(messageId);
        return;
      }
      // Taken on from the wake, which the shell tells the person of. The
      // reply's later pauses come on this page's own continue: the same task.
      this.deps.fromWake(messageId);
      if (this.begin(chat, message, messageId, taking, false)) this.deps.dispatched(messageId);
    } catch {
      if (still()) this.taking.delete(messageId);
    }
  }

  /** A reply was stopped, or the thread forgotten (`messageId` null). */
  cancel(frame: { chatId: string; messageId: string | null }): void {
    const { chatId, messageId } = frame;
    if (!this.info) return;
    this.deps.stopped(chatId);
    const ending = new Set<string>();
    const turn = this.deps.turn(chatId);
    if (messageId) ending.add(messageId);
    else if (turn?.messageId) ending.add(turn.messageId);
    for (const [id, t] of this.taking) {
      if (t.chatId !== chatId || (messageId && id !== messageId)) continue;
      t.unwatch?.();
      this.taking.delete(id);
      ending.add(id);
    }
    this.deps.abandon(chatId, messageId);
    for (const id of ending) this.deps.ended(id);
  }

  /** A sheet asking about the call run as `runId` opened or was answered. */
  asking(runId: string, on: boolean): void {
    if (!on) {
      this.approvals.delete(runId);
      this.askingNow.delete(runId);
    }
    const run = splitRunId(runId);
    if (!run || !this.takingOn(run.messageId)) return;
    if (on) {
      this.askingNow.add(runId);
      this.reportAsking(run, this.approvals.get(runId));
    } else {
      this.send({ messageId: run.messageId, state: 'running' });
    }
  }

  /** The shell made a request that a paired phone may answer a sheet, just
   *  before it asks, and again with a new nonce while it waits. Kept for the
   *  call's asking report, or reported now when that has gone. */
  approvalRequest(json: string): void {
    const request = parseRequest(json);
    const run = request && splitRunId(request.callId);
    if (!request || !run || !this.takingOn(run.messageId)) return;
    this.approvals.set(request.callId, request);
    if (this.askingNow.has(request.callId)) this.reportAsking(run, request);
  }

  /** A paired phone answered one of this PC's sheets: the shell checks it. */
  approval(frame: ApprovalFrame): void {
    if (!this.info || this.stopped) return;
    void this.deps.approve(frame).catch(() => null);
  }

  /** A phone asks to be paired: the shell checks it, and the Worker hears
   *  what it made of it. Nothing where the shell cannot pair. */
  async pair(frame: PairFrame): Promise<void> {
    const info = this.info;
    if (!info || this.stopped) return;
    const verdict = await this.deps.pair(frame).catch(() => null);
    if (verdict) await this.deps.answerPairing(info.id, frame.phone, verdict.ok).catch(() => undefined);
  }

  /** Only a reply this page is taking on in the PC's thread: a sheet asked
   *  for a chat the person is having at the PC is the Worker's to know
   *  nothing of. */
  private takingOn(messageId: string): boolean {
    if (!this.info || !this.chatId) return false;
    const turn = this.deps.turn(this.chatId);
    return turn?.phase === 'streaming' && turn.messageId === messageId;
  }

  /** `asking`, with the shell's request when it made one. One the Worker
   *  refuses still leaves the asking reported, without it. */
  private reportAsking(run: { messageId: string; callId: string }, approval: ApprovalRequest | undefined): void {
    const asking: DeviceReport = { messageId: run.messageId, state: 'asking', callId: run.callId };
    this.send(approval ? { ...asking, approval } : asking, approval ? asking : undefined);
  }

  /** A report, after those before it; `instead` when it is refused as unreadable. */
  private send(body: DeviceReport, instead?: DeviceReport): void {
    const id = this.info!.id;
    this.reports = this.reports
      .then(() => this.deps.report(id, body))
      .catch((err: unknown) => (instead && status(err) === 400 ? this.deps.report(id, instead) : undefined))
      .catch(() => undefined);
  }

  /** Send what the PC is and what it runs. */
  private async publish(): Promise<void> {
    const info = (await this.deps.info()) ?? this.info;
    if (!info) throw new Error('This is not a PC.');
    this.info = info;
    const device = await this.deps.put(info.id, { name: info.name, platform: info.platform, tools: (await this.deps.tools()) ?? [] });
    this.chatId = device.chatId ?? this.chatId;
    this.deps.refused(null);
  }

  private async republish(): Promise<void> {
    for (let attempt = 0; attempt < REPUBLISH_TRIES && !this.stopped; attempt++) {
      try {
        return await this.publish();
      } catch (err) {
        if (!retryable(err)) return this.deps.refused(refusalText(err));
        await sleep(backoffMs(attempt));
      }
    }
  }

  /** `running`, which clears why the reply waited. False when the reply went
   *  on or was stopped elsewhere, so there is nothing to take on. A 403 is a
   *  session that is no longer the PC's: publish again, and once more. */
  private async reportRunning(messageId: string): Promise<boolean> {
    const id = this.info!.id;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await this.deps.report(id, { messageId, state: 'running' });
        return true;
      } catch (err) {
        if (status(err) === 404 || status(err) === 409) return false;
        if (status(err) === 403 && attempt === 0) {
          await this.publish().catch(() => undefined);
          continue;
        }
        // The continue says whether it can go on.
        return true;
      }
    }
    return true;
  }

  /** Start the continue, and watch it for a refusal. False where nothing
   *  was started. */
  private begin(chat: Chat, message: Message, messageId: string, taking: Taking, again: boolean): boolean {
    if (!this.deps.takeOn(chat, message, again)) {
      this.taking.delete(messageId);
      return false;
    }
    taking.unwatch?.();
    taking.unwatch = this.deps.watch(chat.id, (turn) => {
      if (this.taking.get(messageId) !== taking) return;
      // The session that publishes is the PC's: refused as not it, publish
      // again and try once more.
      if (turn?.phase === 'failed' && turn.failure?.refused && turn.failure.code === 'forbidden' && !taking.retried) {
        taking.retried = true;
        taking.unwatch?.();
        taking.unwatch = null;
        void this.publish()
          .catch(() => undefined)
          .then(() => this.taking.get(messageId) === taking && this.begin(chat, message, messageId, taking, true));
        return;
      }
      if (!turn || turn.phase !== 'streaming') {
        taking.unwatch?.();
        this.taking.delete(messageId);
      }
    });
    return true;
  }
}

/** The shell's request, kept as it came for the Worker to check; null for
 *  anything without a run id. */
function parseRequest(json: string): ApprovalRequest | null {
  try {
    const v = JSON.parse(json) as unknown;
    return v && typeof v === 'object' && typeof (v as ApprovalRequest).callId === 'string' ? (v as ApprovalRequest) : null;
  } catch {
    return null;
  }
}
